import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Link } from "wouter";
import AgentLayout from "@/layouts/AgentLayout";
import { useListAgentMembers, useListAgentProviders, useGetMyAgentInvitations, useAcceptAgentInvitation } from "@/lib/api-client-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Check, Mail, TrendingUp, Users, Activity, FileText, LayoutDashboard } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, AreaChart, Area } from 'recharts';
import { useQueryClient } from '@tanstack/react-query';

export default function AgentDashboard() {
  const [user, setUser] = useState<any>(null);
  useEffect(() => {
    const u = localStorage.getItem("user");
    if (u) {
      try { setUser(JSON.parse(u)); } catch(e) {}
    }
  }, []);

  const { data: members, isLoading: loadingMembers } = useListAgentMembers();
  const { data: providers, isLoading: loadingProviders } = useListAgentProviders();
  const { data: invitations, isLoading: loadingInvitations } = useGetMyAgentInvitations(user?.id, { enabled: !!user?.id });
  const [dismissedInvites, setDismissedInvites] = useState<Record<string, boolean>>({});
  const acceptMutation = useAcceptAgentInvitation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleAccept = (id: string) => {
    acceptMutation.mutate(id, {
      onSuccess: () => {
        toast({ title: "Invitation Accepted", description: "You have joined the agency." });
        queryClient.invalidateQueries();
      },
      onError: (error: any) => {
        toast({ title: "Error", description: error.message, variant: "destructive" });
      }
    });
  };

  const chartData = useMemo(() => {
    const data = [];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    // Get the last 6 months
    const today = new Date();
    for (let i = 5; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      data.push({
        name: months[d.getMonth()],
        month: d.getMonth(),
        year: d.getFullYear(),
        members: 0,
        providers: 0
      });
    }

    if (members) {
      members.forEach((m: any) => {
        const d = new Date(m.joinedAt);
        const item = data.find(x => x.month === d.getMonth() && x.year === d.getFullYear());
        if (item) item.members++;
      });
    }

    if (providers) {
      providers.forEach((p: any) => {
        const d = new Date(p.joinedAt);
        const item = data.find(x => x.month === d.getMonth() && x.year === d.getFullYear());
        if (item) item.providers++;
      });
    }

    return data;
  }, [members, providers]);

  return (
    <AgentLayout>
      <div className="w-full space-y-5 sm:space-y-6 pb-12 sm:pb-16 flex flex-col min-h-[calc(100vh-10rem)] relative">
        <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10">
          <div className="absolute top-0 -left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute top-40 -right-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        </div>
        
        {/* Header Section */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 sm:pb-5 border-b border-border/60">
          <div className="flex items-center gap-3 sm:gap-3.5 min-w-0">
            <div className="w-11 h-11 sm:w-12 sm:h-12 bg-primary/10 rounded-xl flex items-center justify-center text-primary border border-primary/20 shrink-0 shadow-xs">
              <LayoutDashboard className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
            </div>
            <div className="space-y-0.5 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight">Overview</h1>
                {user?.role && (
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-primary/10 text-primary border border-primary/20 px-2 py-0.5 rounded-full shadow-2xs">
                    {user.role}
                  </span>
                )}
              </div>
              <p className="text-muted-foreground text-xs sm:text-sm font-normal">
                Welcome back, <span className="font-semibold text-foreground">{user?.displayName || (user?.firstName ? `${user.firstName} ${user.lastName}` : "Agent")}</span>. Here's what's happening with your clients today.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0 w-full sm:w-auto">
            <Button className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white rounded-xl h-10 px-4 shadow-sm shadow-primary/25 hover:shadow-md hover:shadow-primary/30 transition-all text-xs sm:text-sm font-semibold gap-2">
              <Activity className="w-4 h-4 mr-2" /> Download Report
            </Button>
          </div>
        </div>
        
        {/* Pending Invitations */}
        {!loadingInvitations && invitations?.data?.length > 0 && (
          <div className="space-y-2.5">
            {invitations.data.map((inv: any) => !dismissedInvites[inv.id] && (
              <motion.div
                key={inv.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full bg-primary/10 border border-primary/25 rounded-xl p-3 sm:p-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 relative shadow-xs"
              >
                <div className="flex flex-wrap items-center gap-2 text-primary font-medium text-xs sm:text-sm pr-7 sm:pr-0">
                  <span>🎉 You have been invited to join an agency ({inv.agency?.user?.displayName || 'An Agency'}).</span>
                  <Button
                    onClick={() => handleAccept(inv.id)}
                    disabled={acceptMutation.isPending}
                    variant="default"
                    size="sm"
                    className="h-7 px-3 text-xs rounded-lg font-semibold shadow-xs shrink-0"
                  >
                    {acceptMutation.isPending ? "Accepting..." : "Join Now"}
                  </Button>
                </div>
                <button
                  onClick={() => setDismissedInvites(prev => ({...prev, [inv.id]: true}))}
                  className="absolute top-2.5 right-2.5 sm:relative sm:top-auto sm:right-auto text-primary/60 hover:text-primary transition-colors p-1 rounded-md hover:bg-primary/10"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                </button>
              </motion.div>
            ))}
          </div>
        )}

        {/* Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-4 sm:p-5 rounded-2xl bg-card border border-border shadow-xs hover:border-primary/40 hover:shadow-md transition-all flex flex-col justify-between">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20 flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5" />
                </div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Connected Members</div>
              </div>
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 flex items-center shadow-2xs">
                <TrendingUp className="w-3 h-3 mr-0.5" /> +12%
              </span>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
              {loadingMembers ? <Skeleton className="w-14 h-8 rounded-lg" /> : (members?.length || 0)}
            </div>
          </div>

          <div className="p-4 sm:p-5 rounded-2xl bg-card border border-border shadow-xs hover:border-primary/40 hover:shadow-md transition-all flex flex-col justify-between">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="flex items-center gap-2.5">
                <div className="w-10 h-10 rounded-xl bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20 flex items-center justify-center shrink-0">
                  <FileText className="w-5 h-5" />
                </div>
                <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Connected Providers</div>
              </div>
              <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 flex items-center shadow-2xs">
                <TrendingUp className="w-3 h-3 mr-0.5" /> +5%
              </span>
            </div>
            <div className="text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
              {loadingProviders ? <Skeleton className="w-14 h-8 rounded-lg" /> : (providers?.length || 0)}
            </div>
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
          <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-4 sm:p-5 shadow-xs flex flex-col">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-5">
              <h3 className="font-bold text-base text-foreground tracking-tight">Growth Overview</h3>
              <select className="text-xs bg-muted/40 border border-border rounded-lg px-2.5 py-1 text-foreground focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none cursor-pointer self-start sm:self-auto">
                <option>Last 6 Months</option>
                <option>This Year</option>
              </select>
            </div>
            <div className="h-[260px] sm:h-[280px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="colorMembers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorProviders" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="text-border" opacity={0.4} />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'currentColor' }} className="text-muted-foreground" dy={10} />
                  <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'currentColor' }} className="text-muted-foreground" />
                  <Tooltip 
                    contentStyle={{
                      borderRadius: '12px',
                      border: '1px solid hsl(var(--border))',
                      backgroundColor: 'hsl(var(--card))',
                      color: 'hsl(var(--foreground))',
                      boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.15)'
                    }}
                  />
                  <Area type="monotone" dataKey="members" stroke="#3b82f6" strokeWidth={2.5} fillOpacity={1} fill="url(#colorMembers)" name="Members" />
                  <Area type="monotone" dataKey="providers" stroke="#8b5cf6" strokeWidth={2.5} fillOpacity={1} fill="url(#colorProviders)" name="Providers" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
          
          <div className="bg-card border border-border rounded-2xl p-4 sm:p-5 shadow-xs flex flex-col">
            <div className="mb-5">
              <h3 className="font-bold text-base text-foreground tracking-tight">Client Distribution</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Breakdown of connected accounts</p>
            </div>
            <div className="flex-1 flex items-center justify-center min-h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData.slice(-3)} margin={{ top: 10, right: 10, left: -20, bottom: 0 }} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="currentColor" className="text-border" opacity={0.4} />
                  <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'currentColor' }} className="text-muted-foreground" />
                  <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: 'currentColor' }} className="text-muted-foreground" />
                  <Tooltip
                    cursor={{ fill: 'transparent' }}
                    contentStyle={{
                      borderRadius: '12px',
                      border: '1px solid hsl(var(--border))',
                      backgroundColor: 'hsl(var(--card))',
                      color: 'hsl(var(--foreground))',
                      boxShadow: '0 4px 20px -2px rgba(0, 0, 0, 0.15)'
                    }}
                  />
                  <Bar dataKey="members" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={10} name="Members" />
                  <Bar dataKey="providers" fill="#8b5cf6" radius={[0, 4, 4, 0]} barSize={10} name="Providers" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Tables Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5">
          {/* Recent Members */}
          <div className="bg-card rounded-2xl border border-border p-4 sm:p-5 shadow-xs flex flex-col">
            <div className="flex items-center justify-between pb-3 mb-2 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                  <Users className="w-4 h-4" />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-foreground">Recent Members</h3>
              </div>
              <Link href="/agent/members" className="text-xs text-primary font-semibold hover:underline">
                View All
              </Link>
            </div>

            {loadingMembers ? (
              <div className="space-y-2.5 py-2">
                <Skeleton className="w-full h-10 rounded-xl" />
                <Skeleton className="w-full h-10 rounded-xl" />
                <Skeleton className="w-full h-10 rounded-xl" />
              </div>
            ) : members && members.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead>
                    <tr className="border-b border-border/60 text-muted-foreground">
                      <th className="py-2.5 px-2.5 font-semibold text-[11px] uppercase tracking-wider">Name</th>
                      <th className="py-2.5 px-2.5 font-semibold text-[11px] uppercase tracking-wider">Email</th>
                      <th className="py-2.5 px-2.5 font-semibold text-[11px] uppercase tracking-wider">Phone</th>
                      <th className="py-2.5 px-2.5 font-semibold text-[11px] uppercase tracking-wider text-right">Joined</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {members.slice(0, 5).map((m: any) => (
                      <tr key={m.id} className="hover:bg-muted/40 transition-colors">
                        <td className="py-2.5 px-2.5">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-[11px] shrink-0 border border-primary/20">
                              {m.name ? m.name[0].toUpperCase() : 'M'}
                            </div>
                            <span className="font-semibold text-foreground truncate max-w-[110px]" title={m.name}>
                              {m.name}
                            </span>
                          </div>
                        </td>
                        <td className="py-2.5 px-2.5 text-muted-foreground truncate max-w-[140px]" title={m.email}>
                          {m.email}
                        </td>
                        <td className="py-2.5 px-2.5 text-muted-foreground whitespace-nowrap">
                          {m.phone || "N/A"}
                        </td>
                        <td className="py-2.5 px-2.5 text-muted-foreground text-right whitespace-nowrap">
                          {new Date(m.joinedAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-muted-foreground text-xs py-8 text-center flex flex-col items-center justify-center">
                <div className="w-10 h-10 rounded-xl bg-muted/60 border border-border/60 flex items-center justify-center mb-2 text-muted-foreground">
                  <Users className="w-5 h-5 opacity-60" />
                </div>
                No members connected yet.
              </div>
            )}
          </div>

          {/* Recent Providers */}
          <div className="bg-card rounded-2xl border border-border p-4 sm:p-5 shadow-xs flex flex-col">
            <div className="flex items-center justify-between pb-3 mb-2 border-b border-border/50">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4" />
                </div>
                <h3 className="text-sm sm:text-base font-bold text-foreground">Recent Providers</h3>
              </div>
              <Link href="/agent/providers" className="text-xs text-primary font-semibold hover:underline">
                View All
              </Link>
            </div>

            {loadingProviders ? (
              <div className="space-y-2.5 py-2">
                <Skeleton className="w-full h-10 rounded-xl" />
                <Skeleton className="w-full h-10 rounded-xl" />
                <Skeleton className="w-full h-10 rounded-xl" />
              </div>
            ) : providers && providers.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left">
                  <thead>
                    <tr className="border-b border-border/60 text-muted-foreground">
                      <th className="py-2.5 px-2.5 font-semibold text-[11px] uppercase tracking-wider">Name</th>
                      <th className="py-2.5 px-2.5 font-semibold text-[11px] uppercase tracking-wider">Specialty</th>
                      <th className="py-2.5 px-2.5 font-semibold text-[11px] uppercase tracking-wider">NPI</th>
                      <th className="py-2.5 px-2.5 font-semibold text-[11px] uppercase tracking-wider text-right">Joined</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border/40">
                    {providers.slice(0, 5).map((p: any) => (
                      <tr key={p.id} className="hover:bg-muted/40 transition-colors">
                        <td className="py-2.5 px-2.5">
                          <div className="flex items-center gap-2 min-w-0">
                            <div className="w-7 h-7 rounded-full bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center font-bold text-[11px] shrink-0 border border-indigo-500/20">
                              {p.name ? p.name[0].toUpperCase() : 'P'}
                            </div>
                            <span className="font-semibold text-foreground truncate max-w-[110px]" title={p.name}>
                              {p.name}
                            </span>
                          </div>
                        </td>
                        <td className="py-2.5 px-2.5 text-muted-foreground truncate max-w-[130px]" title={p.specialty}>
                          {p.specialty}
                        </td>
                        <td className="py-2.5 px-2.5 text-muted-foreground whitespace-nowrap font-mono text-[11px]">
                          {p.npiNumber || "N/A"}
                        </td>
                        <td className="py-2.5 px-2.5 text-muted-foreground text-right whitespace-nowrap">
                          {new Date(p.joinedAt).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-muted-foreground text-xs py-8 text-center flex flex-col items-center justify-center">
                <div className="w-10 h-10 rounded-xl bg-muted/60 border border-border/60 flex items-center justify-center mb-2 text-muted-foreground">
                  <FileText className="w-5 h-5 opacity-60" />
                </div>
                No providers connected yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </AgentLayout>
  );
}
